<template>
    <!-- The pill is created with its first message inside, so it cannot be the live region
         itself; this stays mounted and only its text changes. -->
    <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ announcement }}</span>
    <Transition name="fade">
        <div
            v-if="userMessageCount <= MAX_MESSAGES"
            class="activation-pill bg-surface-raised text-ink rounded-full border-2 border-border px-6 py-3 shadow-md transition-opacity min-w-72"
            :class="{ 'opacity-0 delay-2500': userMessageCount === MAX_MESSAGES }">
            <div class="details flex justify-between gap-4 mb-1">
                <Transition name="slide" mode="out-in">
                    <span :key="currentMessageShown">{{ currentMessageShown }}</span>
                </Transition>
                <span>{{ userMessageCount }}/{{ MAX_MESSAGES }}</span>
            </div>

            <BaseProgressBar :value="percentComplete" size="sm" :aria-label="`Chat activation: ${userMessageCount} of ${MAX_MESSAGES} messages`" />
        </div>
    </Transition>
</template>

<script lang="ts">
import type { ChatMessage } from "~vue/types/chat";
import { tonalThemeEnabled } from "~vue/utils/theme";

const MAX_MESSAGES = 5;

const countUserMessages = (messages: ChatMessage[]) => messages.filter((m) => m.role === "user" && !m.hidden).length;

// The prototype (tonal) thread carries no activation pill, so it only renders on the legacy theme.
export function isActivationPillShown(messages: ChatMessage[]): boolean {
    return !tonalThemeEnabled.value && countUserMessages(messages) <= MAX_MESSAGES;
}
</script>

<script setup lang="ts">
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import { computed } from "vue";

type ChatActivationProps = {
    messages: ChatMessage[];
};

const { messages = [] } = defineProps<ChatActivationProps>();

const MESSAGES = ["I'm here to listen", "This is helpful to hear", "Interesting, tell me more", "Trying to understand the root cause", "Forming my thoughts", "Ok, I'm ready"];

const userMessageCount = computed(() => countUserMessages(messages));
const currentMessageShown = computed(() => MESSAGES[userMessageCount.value]);
const percentComplete = computed(() => (userMessageCount.value / MAX_MESSAGES) * 100);
const announcement = computed(() => (userMessageCount.value <= MAX_MESSAGES ? `${currentMessageShown.value} ${userMessageCount.value}/${MAX_MESSAGES}` : ""));
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-leave-active,
.slide-enter-active {
    transition:
        opacity var(--motion-duration-slow) var(--motion-ease-enter),
        transform var(--motion-duration-slow) var(--motion-ease-enter);
}

.slide-leave-from,
.slide-enter-to {
    opacity: 1;
    transform: translateY(0);
}

.slide-enter-from {
    opacity: 0;
    transform: translateY(-1lh);
}
.slide-leave-to {
    opacity: 0;
    transform: translateY(1lh);
}

@media (prefers-reduced-motion: reduce) {
    .slide-leave-active,
    .slide-enter-active {
        transition-property: opacity;
    }

    .slide-enter-from,
    .slide-leave-to {
        transform: none;
    }
}
</style>
